import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { WidgetEmptyErrorPlaceholder } from '@ann/web-components';
import { Container } from './WidgetStatePlaceholder.styled';
import { CONNECTION_ERROR_CODES } from './constants/widgetStateConstants';

const isConnectionError = (errorCode, errorMessage) =>
  CONNECTION_ERROR_CODES.includes(errorCode) ||
  errorMessage?.toLowerCase?.().includes('network');

const WidgetStatePlaceholder = ({
  error,
  errorCode,
  isEmpty,
  stateCopy,
  onRetry,
  onEmptyAction,
  canRetry = true,
}) => {
  const config = useMemo(() => {
    if (error) {
      const connectionErr = isConnectionError(errorCode, error?.message);
      return connectionErr ? stateCopy.CONNECTION_ERROR : stateCopy.SYSTEM_ERROR;
    }
    if (isEmpty) {
      return stateCopy.EMPTY;
    }
    return null;
  }, [error, errorCode, isEmpty, stateCopy]);

  if (!config) return null;

  const showButton = error ? canRetry : (config.showButton ?? false);
  const onClick = error ? onRetry : (config.showButton ? onEmptyAction : undefined);

  return (
    <Container>
      <WidgetEmptyErrorPlaceholder
        icon={config.icon}
        title={config.title}
        message={config.message}
        buttonText={config.buttonText}
        showButton={showButton}
        onClick={onClick}
      />
    </Container>
  );
};

WidgetStatePlaceholder.propTypes = {
  error: PropTypes.object,
  errorCode: PropTypes.string,
  isEmpty: PropTypes.bool,
  stateCopy: PropTypes.shape({
    CONNECTION_ERROR: PropTypes.shape({
      icon: PropTypes.string,
      title: PropTypes.string,
      message: PropTypes.string,
      buttonText: PropTypes.string,
    }),
    SYSTEM_ERROR: PropTypes.shape({
      icon: PropTypes.string,
      title: PropTypes.string,
      message: PropTypes.string,
      buttonText: PropTypes.string,
    }),
    EMPTY: PropTypes.shape({
      icon: PropTypes.string,
      title: PropTypes.string,
      message: PropTypes.string,
      buttonText: PropTypes.string,
      showButton: PropTypes.bool,
    }),
  }).isRequired,
  onRetry: PropTypes.func,
  onEmptyAction: PropTypes.func,
  canRetry: PropTypes.bool,
};

WidgetStatePlaceholder.defaultProps = {
  error: null,
  errorCode: '',
  isEmpty: false,
  onRetry: undefined,
  onEmptyAction: undefined,
  canRetry: true,
};

export default WidgetStatePlaceholder;
